import React, { useEffect, useState } from "react";
import {
  useNavigate
} from "react-router-dom";
import {
  ArrowLeft,
  Play,
  CalendarDays
} from "lucide-react";
import * as ownerApi from "../../api/payroll/payrollApi";
import * as portalApi from "../../api/payroll/payrollPortalApi";
import * as academicYearApi from "../../api/academicyear/academicYearApi";
import {
  Button,
  IconButton,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Input,
  Select
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2000, i, 1).toLocaleString("default", { month: "long" }),
}));

export default function PayrollRunCreate({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const basePath = isPortal ? "/staff-panel/payroll-runs" : "/payroll-runs";
  const navigate = useNavigate();

  const now = new Date();
  const currentYear = now.getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  const [form, setForm] = useState({
    month: now.getMonth() + 1,
    year: currentYear,
    academicYearId: "",
  });
  const [activeYearLabel, setActiveYearLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = isPortal
          ? await academicYearApi.getActiveAcademicYearPortal()
          : await academicYearApi.getActiveAcademicYear();
        const year = result?.data ?? result;
        if (!cancelled && year?._id) {
          setForm((prev) => ({ ...prev, academicYearId: year._id }));
          setActiveYearLabel(year.yearName || year.name || year._id);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.message ||
              "No active academic year. Activate one under Academic Years first."
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal, api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.academicYearId) {
      toast.error("Active academic year is required");
      return;
    }
    setLoading(true);
    try {
      const res = await api.generatePayrollRun(form);
      toast.success(res.message || "Payroll run generated");
      const runId = res.data?.payrollRun?._id || res.data?._id;
      setTimeout(
        () => navigate(runId ? `${basePath}/${runId}` : basePath),
        1000
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6 p-4 sm:p-6">
      {/* Header — back action sits inline with the title, not stacked above it */}
      <div className="flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <IconButton
          aria-label="Back to payroll runs"
          variant="secondary"
          size="sm"
          onClick={() => navigate(basePath)}
          className="shrink-0"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </IconButton>
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
            Generate Payroll Run
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Process staff pay for a month against the active academic year
          </p>
        </div>
      </div>

      
      

      <Card>
        <CardHeader className="flex flex-row items-center gap-1.5 bg-neutral-50/50">
          <CalendarDays className="h-4 w-4 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
          <CardTitle>Run Details</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Month"
                options={MONTH_OPTIONS}
                value={form.month}
                onChange={(val) => setForm((prev) => ({ ...prev, month: Number(val) }))}
              />

              <Select
                label="Year"
                options={yearOptions.map((y) => ({ label: String(y), value: y }))}
                value={form.year}
                onChange={(val) => setForm((prev) => ({ ...prev, year: Number(val) }))}
              />
            </div>

            <div>
              <Input
                label="Academic Year"
                readOnly
                value={
                  activeYearLabel
                    ? activeYearLabel
                    : form.academicYearId
                    ? form.academicYearId
                    : "Loading active year…"
                }
                className="bg-neutral-50 dark:bg-neutral-950 text-neutral-700 dark:text-neutral-200"
              />
              <p className="mt-1.5 text-xs text-neutral-400 dark:text-neutral-500">
                Auto-filled from the school's active academic year.
              </p>
            </div>

            <div className="flex flex-col gap-3 border-t border-neutral-200 dark:border-neutral-800 pt-4 sm:flex-row sm:items-center">
              <Button
                type="submit"
                variant="primary"
                isLoading={loading}
                disabled={!form.academicYearId}
                leftIcon={<Play className="h-4 w-4" />}
                fullWidth
                className="sm:w-auto"
              >
                {loading ? "Generating..." : "Generate Run"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(basePath)}
                fullWidth
                className="sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}